import type { Component } from "replo-runtime";
import type { SolidOrGradient } from "replo-runtime/shared/types";
import type { ContextRef, ProductRef } from "schemas/product";

import { getCurrentComponentContext } from "replo-runtime/shared/utils/context";
import {
  doesComponentHaveConnectedDynamicDataProps,
  doesComponentSupportDynamicDataProps,
  getPrimaryDynamicDataProp,
} from "replo-runtime/shared/utils/dynamic-data";
import { evaluateVariableAsString } from "replo-runtime/store/ReploVariable";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

import useApplyComponentAction from "./useApplyComponentAction";

const useDynamicDataQuickConnect = (component: Component | null) => {
  const applyComponentAction = useApplyComponentAction();
  if (!component) {
    return {
      supportsDynamicData: false,
      isDynamicDataConnected: false,
    };
  }
  const supportsDynamicData = doesComponentSupportDynamicDataProps(component);
  const isDynamicDataConnected =
    doesComponentHaveConnectedDynamicDataProps(component);
  const dynamicDataProp = getPrimaryDynamicDataProp(component);
  const handleChangePropValue = (
    newPropValue:
      | string
      | ContextRef
      | ProductRef
      | ProductRef[]
      | SolidOrGradient,
  ) => {
    if (!dynamicDataProp) {
      return null;
    }
    const { propKey, targetType } = dynamicDataProp;
    exhaustiveSwitch({ type: targetType })({
      text: () => {
        applyComponentAction({
          componentId: component.id,
          type: "setProps",
          value: {
            [propKey]: newPropValue,
          },
        });
      },
      productOnly: () => {
        applyComponentAction({
          componentId: component.id,
          type: "setProps",
          value: {
            [propKey]: newPropValue,
          },
        });
      },
      productVariant: () => {
        applyComponentAction({
          componentId: component.id,
          type: "setProps",
          value: {
            [propKey]: newPropValue,
          },
        });
      },
      sellingPlan: () => {
        applyComponentAction({
          componentId: component.id,
          type: "setProps",
          value: {
            [propKey]: newPropValue,
          },
        });
      },
      "text/color": () => {
        applyComponentAction({
          componentId: component.id,
          type: "setStyles",
          value: {
            color: "red",
          },
        });
      },
      "text/url": () => {
        applyComponentAction({
          componentId: component.id,
          type: "setProps",
          value: {
            [propKey]: newPropValue,
          },
        });
      },
      list: () => {
        const transformedValue = {
          type: "inline",
          valueType: "dynamic",
          dynamicPath: newPropValue,
        };
        applyComponentAction({
          componentId: component.id,
          type: "setProps",
          value: {
            [propKey]: transformedValue,
          },
        });
      },
      imageList: () => {
        const transformedValue = {
          type: "inline",
          valueType: "dynamic",
          dynamicPath: newPropValue,
        };
        applyComponentAction({
          componentId: component.id,
          type: "setProps",
          value: {
            [propKey]: transformedValue,
          },
        });
      },
      "text/integer": () => {
        applyComponentAction({
          componentId: component.id,
          type: "setProps",
          value: {
            [propKey]: newPropValue,
          },
        });
      },
      quantity: () => {
        applyComponentAction({
          componentId: component.id,
          type: "setProps",
          value: {
            [propKey]: newPropValue,
          },
        });
      },
      image: () => {
        applyComponentAction({
          componentId: component.id,
          type: "setStyles",
          value: {
            [propKey]: newPropValue,
          },
        });
      },
    });
  };
  const handleRemovePropValue = () => {
    if (!dynamicDataProp || !dynamicDataProp.propValue) {
      return;
    }
    const { propKey, targetType } = dynamicDataProp;

    exhaustiveSwitch({ type: targetType })({
      text: () => {
        const componentContext = getCurrentComponentContext(component.id);
        const evaluatedVariable =
          dynamicDataProp.propValue &&
          evaluateVariableAsString(
            dynamicDataProp.propValue as string,
            componentContext,
          );
        if (!evaluatedVariable) {
          applyComponentAction({
            componentId: component.id,
            type: "deleteProps",
            propName: propKey,
          });
        }
        applyComponentAction({
          componentId: component.id,
          type: "setProps",
          value: {
            [propKey]: evaluatedVariable,
          },
        });
      },
      productOnly: () => {
        applyComponentAction({
          componentId: component.id,
          type: "deleteProps",
          propName: propKey,
        });
      },
      productVariant: () => {
        applyComponentAction({
          componentId: component.id,
          type: "deleteProps",
          propName: propKey,
        });
      },
      sellingPlan: () => {
        applyComponentAction({
          componentId: component.id,
          type: "deleteProps",
          propName: propKey,
        });
      },
      "text/color": () => {
        return null;
      },
      "text/url": () => {
        const componentContext = getCurrentComponentContext(component.id);
        const evaluatedVariable =
          dynamicDataProp.propValue &&
          evaluateVariableAsString(
            dynamicDataProp.propValue as string,
            componentContext,
          );
        if (!evaluatedVariable) {
          return;
        }
        applyComponentAction({
          componentId: component.id,
          type: "setProps",
          value: {
            [propKey]: evaluatedVariable,
          },
        });
      },
      list: () => {
        applyComponentAction({
          componentId: component.id,
          type: "deleteProps",
          propName: propKey,
        });
      },
      imageList: () => {
        applyComponentAction({
          componentId: component.id,
          type: "deleteProps",
          propName: propKey,
        });
      },
      "text/integer": () => {
        const componentContext = getCurrentComponentContext(component.id);
        const evaluatedVariable =
          dynamicDataProp.propValue &&
          evaluateVariableAsString(
            dynamicDataProp.propValue as string,
            componentContext,
          );
        if (!evaluatedVariable) {
          return;
        }
        applyComponentAction({
          componentId: component.id,
          type: "setProps",
          value: {
            [propKey]: evaluatedVariable,
          },
        });
      },
      quantity: () => {
        const componentContext = getCurrentComponentContext(component.id);
        const evaluatedVariable =
          dynamicDataProp.propValue &&
          evaluateVariableAsString(
            dynamicDataProp.propValue as string,
            componentContext,
          );
        if (!evaluatedVariable) {
          applyComponentAction({
            componentId: component.id,
            type: "deleteProps",
            propName: propKey,
          });
        }
        applyComponentAction({
          componentId: component.id,
          type: "setProps",
          value: {
            [propKey]: evaluatedVariable,
          },
        });
      },
      image: () => {
        const componentContext = getCurrentComponentContext(component.id);
        const evaluatedVariable =
          dynamicDataProp.propValue &&
          evaluateVariableAsString(
            dynamicDataProp.propValue as string,
            componentContext,
          );
        if (!evaluatedVariable) {
          applyComponentAction({
            type: "applyCompositeAction",
            value: [
              {
                type: "setStyles",
                value: { __imageSource: null },
              },
              {
                type: "deleteProps",
                propName: "src",
                analyticsExtras: { actionType: "edit", createdBy: "replo" },
              },
            ],
          });
        }
        applyComponentAction({
          componentId: component.id,
          type: "setStyles",
          value: {
            [propKey]: evaluatedVariable,
          },
        });
      },
    });
  };
  return {
    supportsDynamicData,
    isDynamicDataConnected,
    dynamicDataProp,
    handleChangePropValue,
    handleRemovePropValue,
  };
};

export default useDynamicDataQuickConnect;
